import React, { useEffect, useMemo, useState } from "react";
import { Search, Sparkles, ArrowRight, RefreshCw } from "lucide-react";
import PageShell from "../components/PageShell";
import { fetchJaladoraFor, fetchJaladoraTable } from "../toolsApi";
import { useToast } from "../hooks/use-toast";

function Ball({ children, accent = "blue", size = "md" }) {
  const sizes = { sm: "w-10 h-10 text-sm", md: "w-14 h-14 text-lg", lg: "w-20 h-20 text-2xl" };
  const accents = {
    blue:   "from-[#1d6cb1] to-[#0e4a7a]",
    gold:   "from-yellow-400 via-orange-400 to-red-500",
    emerald:"from-emerald-500 to-teal-600",
    purple: "from-fuchsia-500 to-purple-600",
  };
  return (
    <span
      className={`inline-flex items-center justify-center ${sizes[size]} rounded-full bg-gradient-to-br ${accents[accent]} text-white font-extrabold shadow-md`}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {children}
    </span>
  );
}

export default function TablaJaladora() {
  const [input, setInput] = useState("");
  const [single, setSingle] = useState(null);
  const [table, setTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchJaladoraTable();
        setTable(r.table || []);
      } catch (e) {
        toast({ title: "Error", description: "No pudimos cargar la tabla." });
      }
    })();
  }, [toast]);

  const lookup = async (e) => {
    e?.preventDefault?.();
    const v = (input || "").trim();
    if (!v) return;
    if (!/^\d{1,2}$/.test(v)) {
      toast({ title: "Número inválido", description: "Ingresa un número entre 0 y 99" });
      return;
    }
    try {
      setLoading(true);
      const r = await fetchJaladoraFor(v);
      setSingle(r);
    } catch (e) {
      toast({ title: "Error", description: "No pudimos consultar el número." });
    } finally {
      setLoading(false);
    }
  };

  const filteredTable = useMemo(() => {
    return table;
  }, [table]);

  return (
    <PageShell
      eyebrow={<><Sparkles className="w-3.5 h-3.5" /> Tabla Jaladora</>}
      title="Tabla Jaladora 00 — 99"
      subtitle="Descubre cuáles números 'jalan' a otros. Combinación de inverso, suma 11/22, complemento y raíz numerológica usada por jugadores expertos."
      accent="blue"
    >
      {/* Buscador */}
      <div className="bg-white rounded-3xl shadow-xl border border-blue-100 p-6 sm:p-8 mb-10">
        <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <label className="block text-xs font-bold uppercase tracking-wider text-[#0e4a7a] mb-2">
              Tu número (00 - 99)
            </label>
            <div className="flex items-center bg-blue-50/60 border-2 border-blue-100 focus-within:border-[#1d6cb1] rounded-xl px-4 py-3">
              <Search className="w-5 h-5 text-[#1d6cb1] mr-2" />
              <input
                data-testid="jaladora-input"
                value={input}
                onChange={(e) => setInput(e.target.value.replace(/\D/g, "").slice(0, 2))}
                placeholder="Ej: 12"
                inputMode="numeric"
                className="flex-1 bg-transparent outline-none text-xl font-extrabold tracking-widest text-[#0e4a7a]"
              />
            </div>
          </div>
          <button
            data-testid="jaladora-submit-btn"
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce] text-white font-bold shadow-md hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 transition"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Ver Jalados
          </button>
        </form>

        {single && (
          <div className="mt-8 grid md:grid-cols-[auto_1fr] gap-6 items-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-[#0e4a7a] mb-2">Tu número</p>
              <Ball size="lg" accent="blue">{single.input}</Ball>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#1d6cb1] font-bold mb-2">Números que jala</p>
              <div className="flex flex-wrap gap-3 mb-4" data-testid="jaladora-pulled-list">
                {single.pulled.map((p, i) => (
                  <Ball key={i} size="md" accent={["gold","emerald","purple","blue"][i%4]}>{p}</Ball>
                ))}
              </div>
              <ul className="space-y-1 text-xs text-gray-700">
                {single.explanations.map((e, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#1d6cb1] font-bold">·</span>{e}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Tabla completa */}
      <div className="bg-white rounded-3xl shadow-xl border border-blue-100 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-extrabold text-[#0e4a7a]">Tabla completa</h2>
          <span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-[#1d6cb1] font-semibold border border-blue-200">100 entradas</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filteredTable.map((row) => (
            <div
              key={row.number}
              className="rounded-xl border border-blue-100 p-3 hover:bg-blue-50/40 transition"
              data-testid={`jaladora-row-${row.number}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-extrabold text-[#0e4a7a] text-lg" style={{fontFamily:"'Playfair Display', serif"}}>{row.number}</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400">jala</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {row.pulled.map((p, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center justify-center min-w-[34px] h-8 px-2 rounded-md bg-gradient-to-br from-blue-100 to-blue-50 text-[#0e4a7a] font-bold text-xs border border-blue-200"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
